import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import ProcessingModal from '../components/ProcessingModal';
import { useProcessingStatus } from '../hooks/useProcessingStatus';
import AppLayout from '../components/Layout/AppLayout';

const ProcessingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { status: processing, loading, error, retry } = useProcessingStatus(id);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(true);
  
  // Track when processing starts
  useEffect(() => {
    if ((processing?.status === 'uploaded' || processing?.status !== 'completed') && !processingStartTime) {
      const startTime = Date.now();
      setProcessingStartTime(startTime);
      console.log('📄 ProcessingPage: Started tracking at', new Date().toLocaleTimeString());
    }
  }, [processing?.status, processingStartTime]);

  // Navigate to results when processing is completed AND minimum time has passed
  useEffect(() => {
    if (processing?.status === 'completed' && processingStartTime) {
      const MINIMUM_DISPLAY_TIME = 32000; // 32 seconds (slightly more than dashboard)
      const elapsed = Date.now() - processingStartTime;
      const minimumTimePassed = elapsed >= MINIMUM_DISPLAY_TIME;
      
      if (minimumTimePassed) {
        console.log('🎯 ProcessingPage: Navigating to results after', Math.floor(elapsed / 1000) + 's');
        setTimeout(() => {
          setShowModal(false);
          navigate(`/talents/${id}`);
        }, 2000); // Extra 2 second delay to see completion
      } else {
        console.log('⏳ ProcessingPage: Waiting for minimum time, elapsed:', Math.floor(elapsed / 1000) + 's');
        // Set a timeout to navigate when minimum time is reached
        const remainingTime = MINIMUM_DISPLAY_TIME - elapsed + 2000;
        setTimeout(() => {
          setShowModal(false);
          navigate(`/talents/${id}`);
        }, remainingTime);
      }
    }
  }, [processing?.status, id, navigate, processingStartTime]);

  const handleRetry = () => {
    retry();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    navigate('/');
  };

  if (loading && !processing) {
    return (
      <AppLayout pageTitle="Processing" breadcrumb="Upload > Processing">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading processing status...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    const isSessionExpired = error.includes('session may have expired') || error.includes('server was restarted');
    
    return (
      <AppLayout pageTitle="Processing Error" breadcrumb="Upload > Error">
        <div className={`min-h-screen bg-gradient-to-br ${isSessionExpired ? 'from-orange-50 to-yellow-100' : 'from-red-50 to-pink-100'} flex items-center justify-center p-4`}>
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
            <ExclamationTriangleIcon className={`w-16 h-16 ${isSessionExpired ? 'text-[var(--warning-color)]' : 'text-[var(--error-color)]'} mx-auto mb-4`} />
            <h2 className="text-[var(--font-size-2xl)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] text-gray-900 mb-2">
              {isSessionExpired ? 'Session Expired' : 'Processing Failed'}
            </h2>
            <p className="text-gray-600 mb-6">
              {isSessionExpired 
                ? 'Your processing session has expired. This can happen when the server restarts. Please upload your file again.'
                : 'We encountered an error while processing your file. Please try again.'}
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/upload')}
                className="w-full"
              >
                {isSessionExpired ? 'Upload Again' : 'Upload New File'}
              </Button>
              {!isSessionExpired && (
                <Button 
                  onClick={handleRetry}
                  variant="outline"
                  className="w-full"
                >
                  Retry Current Session
                </Button>
              )}
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!processing) {
    return (
      <AppLayout pageTitle="Processing" breadcrumb="Upload > Processing">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <p className="text-gray-500">No processing status found.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Show processing modal for processing and completed status
  return (
    <AppLayout pageTitle="Processing" breadcrumb="Upload > Processing">
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <h2 className="text-[var(--font-size-2xl)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] text-gray-900 mb-2">File Uploaded Successfully</h2>
          <p className="text-gray-600">
            Your file has been uploaded and processing has started in the background.
          </p>
        </div>
      </div>
      
      <ProcessingModal 
        isOpen={showModal}
        onClose={handleCloseModal}
        fileName={id}
        sessionId={id}
      />
    </AppLayout>
  );
};

export default ProcessingPage;